import { CheckOption, Field, Select, Textarea } from '@wundo/ui';
import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type { NotificationKind } from '@/lib/notificationSend.ts';
import { CHANNEL_LABELS } from './sendLabels.ts';

const KIND_GROUP_LABELS: Record<NotificationKind['group'], string> = {
  moment: 'Wundo’s moments',
  email: 'Emails',
};

/* Moments first, then the mails: two lists that read differently in the picker. */
function kindOptions(kinds: NotificationKind[]) {
  return (['moment', 'email'] as const)
    .map((group) => ({
      label: KIND_GROUP_LABELS[group],
      options: kinds
        .filter((option) => option.group === group)
        .map((option) => ({ value: option.kind, label: option.label })),
    }))
    .filter((group) => group.options.length > 0);
}

interface KindFieldsProps {
  kinds: NotificationKind[];
  kind: string;
  entry: NotificationKind | undefined;
  isEmail: boolean;
  takesNeverName: boolean;
  neverNameText: string;
  neverNameError: string;
  channel: SendableNotificationChannel | undefined;
  resend: boolean;
  onKindChange: (kind: string) => void;
  onNeverNameChange: (text: string) => void;
  onNeverNameBlur: () => void;
  onChannelChange: (channel: SendableNotificationChannel | '') => void;
  onResendChange: (resend: boolean) => void;
}

export function KindFields(props: KindFieldsProps) {
  const { kinds, kind, entry, isEmail, onKindChange } = props;

  return (
    <>
      <Field label='Notification' required htmlFor='kind'>
        <Select
          id='kind'
          wrapperClassName='w-full'
          className='w-full'
          value={kind}
          onChange={(event) => onKindChange(event.target.value)}
          options={[
            { value: '', label: 'Choose a notification...' },
            ...kindOptions(kinds),
          ]}
        />
      </Field>

      {props.takesNeverName && (
        <NeverNameField
          text={props.neverNameText}
          error={props.neverNameError}
          onChange={props.onNeverNameChange}
          onBlur={props.onNeverNameBlur}
        />
      )}

      {entry && isEmail && <MailChannelField label={entry.label} />}

      {entry && !isEmail && (
        <>
          <ChannelField
            channels={entry.channels}
            value={props.channel}
            onChange={props.onChannelChange}
          />
          <ResendOption checked={props.resend} onChange={props.onResendChange} />
        </>
      )}
    </>
  );
}

interface NeverNameFieldProps {
  text: string;
  error: string;
  onChange: (text: string) => void;
  onBlur: () => void;
}

function NeverNameField({ text, error, onChange, onBlur }: NeverNameFieldProps) {
  return (
    <Field
      label='Never name these people'
      description='E-mail addresses, one per line. The tip leaves them out of every question, for everybody it goes to.'
      error={error}
    >
      {(control) => (
        <Textarea
          {...control}
          rows={2}
          placeholder='name@company.com'
          value={text}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}

/* A mail has one channel, so there is nothing to choose and nothing to pass. */
function MailChannelField({ label }: { label: string }) {
  return (
    <Field
      label='Channel'
      htmlFor='channel'
      description={`${label} only exists as an email.`}
    >
      <Select
        id='channel'
        wrapperClassName='w-full'
        className='w-full'
        value='email'
        onChange={() => undefined}
        options={[{ value: 'email', label: CHANNEL_LABELS.email }]}
      />
    </Field>
  );
}

interface ChannelFieldProps {
  channels: SendableNotificationChannel[];
  value: SendableNotificationChannel | undefined;
  onChange: (channel: SendableNotificationChannel | '') => void;
}

function ChannelField({ channels, value, onChange }: ChannelFieldProps) {
  return (
    <Field
      label='Channel'
      htmlFor='channel'
      description='Send it on this channel, whatever channel each person reads Wundo on. Whoever cannot be reached there is left out.'
    >
      <Select
        id='channel'
        wrapperClassName='w-full'
        className='w-full'
        value={value ?? ''}
        onChange={(event) =>
          onChange(event.target.value as SendableNotificationChannel | '')
        }
        options={[
          { value: '', label: 'User’s channel' },
          ...channels.map((option) => ({
            value: option,
            label: CHANNEL_LABELS[option],
          })),
        ]}
      />
    </Field>
  );
}

/* The option names itself: a field label around it would replace its name. */
function ResendOption({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (resend: boolean) => void;
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <CheckOption
        label='Also send to people who already received it'
        aria-describedby='resend-description'
        checked={checked}
        onCheckedChange={onChange}
      />
      <p id='resend-description' className='text-sm text-ink-muted'>
        Each moment reaches a person once. Tick this to send it again to whoever already
        got it. Everyone left out for another reason stays left out.
      </p>
    </div>
  );
}
